import { useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Spinner } from '@/components/Spinner';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { recordScreening } from '@/services/screening';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';
import { useScrollToFirstError } from '@/shared/hooks/useScrollToFirstError';

import { useScreeningQuestions } from '../hooks/useScreeningQuestions';
import type { ScreeningAnswers } from '../screeningAnswers';
import { answerOf, choose, isComplete, toNote, typeFollowUp } from '../screeningAnswers';
import type { ScreeningSet } from '../types';
import { ScreeningQuestionControl } from './ScreeningQuestionControl';

/**
 * The fit questions: ISS's stored screening set, asked as stored [03-us-kpw-3a; KPW-3; decisions 0217,
 * 0330, 0364, 0417].
 *
 * **The questions are not in this file.** They are read from `GET /screening/questions` — the same rows the
 * admin's *Screening* tab shows [KAD-1i] — and rendered in the kiosk's language. The three placeholders this
 * screen carried, and their *Confirm your shift* row, are gone [AC-5, D8].
 *
 * ⭐ **The invariant** [KPW-3a]: what the candidate is asked is exactly the stored set, and what the recruiter
 * reads is exactly what the candidate answered. So there are exactly three states, and no fourth:
 * the read in flight (the app's loading treatment), the set (every control), and a set that cannot be read
 * — which hands the candidate to a person and asks NOTHING, because a fallback question on screen becomes a
 * note recording answers to questions ISS never asked [AC-11, D10].
 *
 * **The kiosk does not judge the answers** [0111]. Every control is required — a completion rule, ruled by
 * the Operator [0364 (d)] — and NO answer changes where the visit goes, Q9's `NO` included [KPW-3b; 0417].
 *
 * **The set closes with the EEO / ADA note** [AC-13; 0367 (e)] — a disclosure, not a control. It is shown
 * only with the set, so the unavailable state has none; it is not in `isComplete`, and `toNote` never sees
 * it, because it is not in the set.
 */
export function FitQuestionsScreen() {
  const outcome = useScreeningQuestions();

  if (outcome === undefined) return <FitQuestionsLoading />;
  if (outcome.kind === 'unavailable') return <FitQuestionsUnavailable />;
  return <FitQuestionsForm set={outcome.set} />;
}

/**
 * The read in flight — the same shape `JobsScreen` uses: spinner, one line, `aria-busy` with a polite live
 * region so the wait is announced once.
 */
function FitQuestionsLoading() {
  const t = useTranslation();
  return (
    <KioskScreen>
      <KioskCard header={<StatusBadge label={t('badge.kiosk')} />}>
        <div
          aria-busy="true"
          aria-live="polite"
          className="flex w-full flex-col items-center gap-4 py-12"
        >
          <Spinner />
          <p className="text-sm leading-6 text-[var(--muted-foreground)]">{t('fit.loading')}</p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}

/**
 * A set that could not be read: the app's neutral *see a representative* hand-off [AC-11, D10].
 *
 * `NoJobsScreen`'s unreachable state, for the same situation — the heading is KID-2's words, and the body
 * promises no person is already coming, because nobody was enqueued. No question is rendered and no note is
 * written; the submission, written before this screen [0073], is untouched. Back returns to the job, and
 * coming forward again is a fresh read.
 */
function FitQuestionsUnavailable() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);

  return (
    <KioskScreen>
      <KioskCard
        band={<Stepper current={JOURNEY_STEP.questions} />}
        footer={
          <Button fullWidth onClick={endCandidateSession} variant="secondary">
            {t('fit.unavailable.done')}
          </Button>
        }
        header={
          <>
            <BackLink onClick={() => goTo(SCREEN.jobDetail)} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-center gap-3 pb-8">
          <h1
            className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
            ref={heading}
            tabIndex={-1}
          >
            {t('fit.unavailable.heading')}
          </h1>
          <p className="w-full text-center text-sm leading-6 text-[var(--muted-foreground)]">
            {t('fit.unavailable.body')}
          </p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}

interface FitQuestionsFormProps {
  readonly set: ScreeningSet;
}

function FitQuestionsForm({ set }: FitQuestionsFormProps) {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const language = useKioskSession((state) => state.language);
  const goTo = useKioskSession((state) => state.goTo);
  const job = useKioskSession((state) => state.selectedJob);
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);

  // By question and option KEY — a language switch re-renders the strings and cannot touch these [AC-7].
  const [answers, setAnswers] = useState<ScreeningAnswers>({});
  const [refusals, setRefusals] = useState(0);
  useScrollToFirstError(refusals);

  const requiredMessage = refusals > 0 ? t('application.answerRequired') : undefined;

  function submit(): void {
    if (!isComplete(set, answers)) {
      setRefusals((count) => count + 1);
      return;
    }

    /*
     * The answers are WRITTEN, never routed on [decision 0111: they reach a recruiter as a note].
     *
     * Fire-and-forget, deliberately not awaited: 0111 says the kiosk does not judge these, so nobody may be
     * held at a screen because a note is slow. A write that did not land is logged HERE, once it settles —
     * `recordScreening` returns the failure as a value and reports nothing itself. ⛔ No candidate detail:
     * it prints on a shared device's console.
     *
     * Skipped without a candidate id, for the same reason the submission is: posting a note against no
     * record would orphan it. That path means the profile write failed, which is already an alert.
     */
    if (candidateId !== undefined) {
      void recordScreening(candidateId, toNote(set, answers), job?.title ?? undefined).then(
        (outcome) => {
          if (outcome.kind === 'recorded') return;
          console.error({
            msg: 'POST /screening did not record the screening note',
            alert: 'kiosk_screening_note_not_written',
            ...(outcome.status === undefined ? {} : { status: outcome.status }),
          });
        },
      );
    }

    // Every answer goes to the note and none of them is a route: a NO on Q9 goes on like every other
    // [03-us-kpw-3b; decision 0417 (a)-(c); 0111].
    goTo(SCREEN.submitted);
  }

  return (
    <KioskScreen>
      <KioskCard
        band={<Stepper current={JOURNEY_STEP.questions} />}
        footer={
          /*
           * The action HUGS its label and sits right from `sm` up, and stretches at 375 — measured across the
           * live frames on 2026-09-02 (122 wide on Fit questions, 343 full-width at 375).
           */
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
            <Button onClick={submit}>{t('fit.continue')}</Button>
          </div>
        }
        header={
          <>
            <BackLink onClick={() => goTo(SCREEN.jobDetail)} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          {/* 8px between the heading and its subtitle: the frame's heading block is 28 + 8 + 48. */}
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('fit.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('fit.subtitle', { job: job?.title ?? '' })}
            </p>
          </div>

          {set.questions.map((question) => (
            <ScreeningQuestionControl
              answer={answerOf(answers, question.key)}
              key={question.key}
              language={language}
              onChoose={(optionKey) => {
                setAnswers((current) => choose(current, question, optionKey));
              }}
              onFollowUp={(optionKey, text) => {
                setAnswers((current) =>
                  typeFollowUp(current, { questionKey: question.key, optionKey }, text),
                );
              }}
              question={question}
              requiredMessage={requiredMessage}
            />
          ))}

          {/*
           * The EEO / ADA note, last in the content, after Q9 — frame `card` `12872:30976`, node `12872:31033`,
           * read over REST 2026-09-25: 12px, line height 145%, `--muted-foreground` (#585756), fill width,
           * 24px below Q9 (Content's gap). Plain text with no role and no tabindex: nothing to answer, and
           * nothing to focus as a question [AC-13]. Legal text, as stored: the same English in both
           * languages [0328; 0367 note 4; 0366].
           */}
          <p className="w-full text-xs leading-[1.45] text-[var(--muted-foreground)]">
            {t('fit.eeoNote')}
          </p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
